import React, { useState, useCallback } from 'react'
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsalAuthentication } from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'
import { Stack, Box, styled, Typography, Chip } from '@kyc-ui/react'
import Loading from 'src/components/common/Loading'
import useTranslation from 'src/hooks/common/useTranslation'
import useTheme from 'src/hooks/common/useTheme'
import BillingPortalForm from 'src/components/BillingPortalForm'
import IgnoreCaseForm from 'src/components/IgnoreCaseForm'

const UsageReport = () => {
  useMsalAuthentication(InteractionType.Redirect)
  const t = useTranslation('usageReport')
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const [isUsageReport, setIsUsageReport] = useState(true)
  const [isIgnoreCases, setIsIgnoreCases] = useState(false)
  const [isRenew, setIsRenew] = useState(false)
  const handleTabChange = useCallback((value: 'usageReport' | 'renew' | 'ignoreCases') => {
    setIsUsageReport(false)
    setIsIgnoreCases(false)
    setIsRenew(false)
    setIsUsageReport(value === 'usageReport')
    setIsIgnoreCases(value === 'ignoreCases')
    setIsRenew(value === 'renew')
  }, [])
  const handleLoading = useCallback((value: boolean) => setLoading(value), [])

  return (
    <>
      <AuthenticatedTemplate>
        <UsageReportBox data-testid="usage-report">
          <TitleBox data-testid="title">
            <Typography align="left" color={theme.palette.secondary.main} variant="body2">
              {t('name')}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Chip
                sx={{ px: 3, py: 2, height: 'auto', borderRadius: '28px' }}
                label={
                  <Typography align="left" variant="h5">
                    {t('title')}
                  </Typography>
                }
                color={isUsageReport ? 'primary' : 'default'}
                onClick={() => handleTabChange('usageReport')}
              />
              <Chip
                sx={{ px: 3, py: 2, height: 'auto', borderRadius: '28px' }}
                label={
                  <Typography align="left" variant="h5">
                    {t('renew')}
                  </Typography>
                }
                color={isRenew ? 'primary' : 'default'}
                onClick={() => handleTabChange('renew')}
              />
              <Chip
                sx={{ px: 3, py: 2, height: 'auto', borderRadius: '28px' }}
                label={
                  <Typography align="left" variant="h5">
                    {t('ignoreCases')}
                  </Typography>
                }
                color={isIgnoreCases ? 'primary' : 'default'}
                onClick={() => handleTabChange('ignoreCases')}
              />
            </Stack>
          </TitleBox>
          {isIgnoreCases ? (
            <IgnoreCaseForm />
          ) : (
            <BillingPortalForm isUsageReport={isUsageReport} onLoading={handleLoading} />
          )}
          {loading && <Loading isGlobal message={t('loading')} />}
        </UsageReportBox>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <UsageReportBox>
          <Typography sx={{ textAlign: 'center', m: '24px auto' }}>Please signin...</Typography>
        </UsageReportBox>
      </UnauthenticatedTemplate>
    </>
  )
}

const UsageReportBox = styled(Box)(({ theme }) => ({
  width: '100%',

  '& .MuiPaper-root': {
    boxShadow: 'none',
    display: 'grid',
    gap: theme.spacing(4)
  }
}))

const TitleBox = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(8),

  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(6)
  }
}))

export default UsageReport
